import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Phone, Mail, MapPin, Menu as MenuIcon, X, ShoppingBag, MessageCircle } from 'lucide-react';
import { useClinicBranding, clinicLogo } from '../../context/ClinicBrandingContext';
import { buildFooterScheduleLines } from '../../lib/scheduleDisplay';
import { displayBrandName } from '../../lib/brand';
import { useStoreStatus } from '../../lib/useStoreStatus';
import { CART_EVENT, readCartCount } from '../../lib/cartEvents';
import { replayClass } from '../../lib/motion';
import StatusPill from './StatusPill';
import DemoSiteBanner from '../legal/DemoSiteBanner';
import CookieConsentBanner from '../legal/CookieConsentBanner';
import LegalFooterBlock from '../legal/LegalFooterBlock';

/** Paquete legal: 10 dígitos = número de México (+52). Antes generaba tel:+8112141456. */
function intlDigits(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length === 10 ? `52${digits}` : digits;
}

export function telHref(phone) {
  const digits = intlDigits(phone);
  return digits.length ? `tel:+${digits}` : '#';
}

/** wa.me con el WhatsApp de «Identidad y contacto» (vacío = el teléfono). */
export function waHref(phone, text = 'Hola, quiero hacer un pedido 🍕') {
  const digits = intlDigits(phone);
  if (!digits) return '';
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

function useCartCount() {
  const [count, setCount] = useState(readCartCount);
  useEffect(() => {
    const on = () => setCount(readCartCount());
    window.addEventListener(CART_EVENT, on);
    window.addEventListener('storage', on);
    return () => {
      window.removeEventListener(CART_EVENT, on);
      window.removeEventListener('storage', on);
    };
  }, []);
  return count;
}

export function PizzeriaHeader() {
  const { info, schedule } = useClinicBranding();
  const status = useStoreStatus();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const count = useCartCount();
  const badgeRef = useRef(null);
  const prevCount = useRef(count);

  const brandName = displayBrandName(info?.name);
  const logo = clinicLogo(info);
  const phone = info?.phone || '';
  const wa = waHref(info?.whatsapp || phone);
  const ctaLabel = info?.cta_button_label || 'Pide tu pizza';
  const ctaPath = info?.cta_button_path || '/pedir';

  const navItems = useMemo(
    () => [
      { name: info?.nav_label_home || 'Inicio', path: '/' },
      { name: info?.nav_label_menu || 'Menú', path: info?.nav_path_menu || '/pedir' },
      { name: info?.nav_label_reviews || 'Reseñas', path: '/resenas' },
      { name: info?.nav_label_contact || 'Contacto', path: '/contacto' },
    ],
    [info],
  );

  useEffect(() => {
    const on = () => setCompact(window.scrollY > 24);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (count > prevCount.current) replayClass(badgeRef.current, 't-bounce');
    prevCount.current = count;
  }, [count]);

  return (
    <>
    {/* Paquete legal: aviso «Sitio de demostración» (doc. 05 §2), cerrable por sesión. Fuera del header
        sticky para no cambiar su altura (las pestañas del menú usan top-[96px]). */}
    <DemoSiteBanner />
    <header className="sticky top-0 z-50" data-testid="main-header">
      {/* Barra de estado */}
      <div className="bg-[#2B1A12] text-[#FFF6EA]">
        <div className="max-w-7xl mx-auto px-4 h-10 flex items-center justify-between gap-3 text-sm">
          <StatusPill status={status} schedule={schedule} className="!py-0.5 text-xs sm:text-sm" />
          <span className="hidden md:inline font-hand text-xl text-[#F2B33D] leading-none">
            2x1 en pizzas · una sola pizza −30%{' '}
            <Link to="/terminos#promociones" className="font-sans text-xs font-semibold text-[#FFF6EA] underline underline-offset-2 hover:no-underline align-middle">
              Ver condiciones
            </Link>
          </span>
          <div className="flex items-center gap-3">
            {phone ? (
              <a href={telHref(phone)} className="inline-flex items-center gap-1.5 font-semibold hover:underline">
                <Phone className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">{phone}</span>
                <span className="sm:hidden">Llamar</span>
              </a>
            ) : null}
            {wa ? (
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#3F7D3A] px-2.5 py-0.5 font-semibold hover:brightness-110"
              >
                <MessageCircle className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">WhatsApp</span>
                <span className="sr-only sm:hidden">WhatsApp</span>
              </a>
            ) : null}
          </div>
        </div>
      </div>

      <nav
        className={`t-header bg-[#FFFDF8]/90 backdrop-blur-md border-b border-[#F1DFC8] ${
          compact ? 'py-1.5 shadow-[0_8px_24px_-16px_rgba(43,26,18,.4)]' : 'py-3'
        }`}
        aria-label="Principal"
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
            <img
              src={logo}
              alt={brandName}
              className={`w-auto object-contain transition-all duration-300 ${compact ? 'h-11' : 'h-14'}`}
            />
            <span className="font-heading text-2xl font-extrabold text-[#2B1A12] hidden sm:inline">{brandName}</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `relative px-3 py-2 font-semibold rounded-full transition-colors ${
                    isActive ? 'text-[#C2410C] bg-[#F2B33D]/20' : 'text-[#2B1A12] hover:text-[#C2410C]'
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/pedir"
              id="t-cart-target-header"
              className="relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#FFF6EA] border border-[#F1DFC8] text-[#2B1A12] hover:border-[#C2410C]"
              aria-label={count ? `Carrito: ${count} artículo${count !== 1 ? 's' : ''}` : 'Carrito vacío, ir al menú'}
            >
              <ShoppingBag className="w-5 h-5" aria-hidden="true" />
              {count > 0 ? (
                <span
                  ref={badgeRef}
                  className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-[#B8321F] text-white text-xs font-bold flex items-center justify-center tabular"
                  aria-hidden="true"
                >
                  {count}
                </span>
              ) : null}
            </Link>
            <Link to={ctaPath} className="t-btn t-btn-primary hidden md:inline-flex" data-testid="book-appointment-btn">
              {ctaLabel}
            </Link>
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-[#2B1A12]"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={open}
              data-testid="mobile-menu-btn"
            >
              {open ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open ? (
          <div className="md:hidden max-w-7xl mx-auto px-4 pb-4 pt-2 animate-fadeIn">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-xl font-semibold text-lg ${isActive ? 'bg-[#F2B33D]/25 text-[#C2410C]' : 'text-[#2B1A12]'}`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
              <Link to={ctaPath} className="t-btn t-btn-primary mt-2 w-full">
                {ctaLabel}
              </Link>
            </div>
          </div>
        ) : null}
      </nav>
    </header>
    </>
  );
}

export function PizzeriaFooter() {
  const { info, schedule } = useClinicBranding();
  const brandName = displayBrandName(info?.name);
  const logo = clinicLogo(info);
  const phone = info?.phone || '';
  const mail = info?.email || '';
  const address = info?.address || '';
  const blurb = info?.footer_blurb || 'Pizzería con amor en cada rebanada.';
  const scheduleLines = useMemo(() => buildFooterScheduleLines(schedule), [schedule]);
  const wa = waHref(info?.whatsapp || phone);

  return (
    <>
    <footer className="bg-[#2B1A12] text-[#FFF6EA]" data-testid="main-footer">
      <div className="t-checker-edge" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <img src={logo} alt={brandName} className="h-16 w-auto object-contain mb-4 rounded-xl bg-white p-1.5" />
            <p className="text-base leading-relaxed text-[#FFF6EA]">{blurb}</p>
            <p className="font-hand text-2xl text-[#F2B33D] mt-3">¡Hecha con amor en cada rebanada!</p>
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl mb-4 text-[#F2B33D]">Horarios</h2>
            <div className="space-y-2 text-base">
              {scheduleLines.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl mb-4 text-[#F2B33D]">Contacto</h2>
            <div className="space-y-3 text-base">
              {phone ? (
                <a href={telHref(phone)} className="flex items-center gap-2 hover:underline">
                  <Phone className="w-4 h-4" aria-hidden="true" /> {phone}
                </a>
              ) : null}
              {wa ? (
                <a href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                  <MessageCircle className="w-4 h-4" aria-hidden="true" /> Pedir por WhatsApp
                </a>
              ) : null}
              {mail ? (
                <a href={`mailto:${mail}`} className="flex items-center gap-2 hover:underline break-all">
                  <Mail className="w-4 h-4 shrink-0" aria-hidden="true" /> {mail}
                </a>
              ) : null}
              {address ? (
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 shrink-0" aria-hidden="true" /> {address}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        {/* Paquete legal (doc. 05 §1): identidad, contacto y enlaces legales relativos */}
        <div className="border-t border-white/15 mt-10 pt-6 text-[#FFF6EA]/90">
          <LegalFooterBlock className="[&_a]:text-[#FFF6EA] [&_button]:text-[#FFF6EA]" />
        </div>
      </div>
    </footer>
    {/* Aviso de cookies: PostHog solo tras «Aceptar» */}
    <CookieConsentBanner />
    </>
  );
}
